import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { FAQS, buildFaqJsonLd } from "../lib/faqData";

/**
 * Homepage FAQ accordion section — single-open at a time, smooth Radix
 * animations, SEO-friendly markup, and an inline FAQPage JSON-LD script
 * tag injected into <head> for Google rich-results eligibility.
 *
 * The JSON-LD is keyed by a stable id ("faq-jsonld-home") so re-renders
 * replace rather than duplicate, and the tag is removed on unmount.
 */
function FaqJsonLd({ id, faqs }) {
  useEffect(() => {
    const tagId = `faq-jsonld-${id}`;
    let el = document.getElementById(tagId);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = tagId;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(buildFaqJsonLd(faqs));
    return () => {
      const t = document.getElementById(tagId);
      if (t && t.parentNode) t.parentNode.removeChild(t);
    };
  }, [id, faqs]);
  return null;
}

function AnswerBody({ lines }) {
  // Lines starting with "•" render as a bullet list; consecutive non-bullet
  // lines render as paragraphs. Keeps copy structure consistent with
  // FaqDetail.jsx and the JSON-LD plain text.
  return (
    <div className="space-y-3 text-[14.5px] text-ink-soft leading-relaxed">
      {(() => {
        const out = [];
        let bullets = [];
        const flush = (key) => {
          if (bullets.length) {
            out.push(
              <ul key={`b-${key}`} className="list-disc pl-5 space-y-1">
                {bullets.map((b, i) => (
                  <li key={i}>{b.replace(/^•\s*/, "")}</li>
                ))}
              </ul>,
            );
            bullets = [];
          }
        };
        lines.forEach((line, i) => {
          if (line.startsWith("•")) {
            bullets.push(line);
          } else {
            flush(i);
            out.push(<p key={`p-${i}`}>{line}</p>);
          }
        });
        flush("end");
        return out;
      })()}
    </div>
  );
}

export default function FaqSection() {
  return (
    <section
      id="faq"
      data-testid="homepage-faq-section"
      className="bg-white py-16 md:py-24 border-t border-surface-line"
    >
      <FaqJsonLd id="home" faqs={FAQS} />
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* Left — sticky header block */}
        <div className="lg:col-span-4" data-animate="fade-up-1">
          <div className="lg:sticky lg:top-28">
            <span className="eyebrow-cap">Frequently asked</span>
            <h2
              data-testid="homepage-faq-headline"
              className="text-[28px] sm:text-4xl lg:text-[42px] font-extrabold tracking-[-0.03em] text-ink mt-4 leading-[1.08]"
            >
              Frequently asked questions
            </h2>
            <p className="text-[14.5px] sm:text-[15px] text-ink-soft/85 mt-4 leading-relaxed max-w-sm">
              Answers about how BlendSafe works, what each verdict means, and how
              to use it alongside your medications and dermatologist.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/faq"
                data-testid="homepage-faq-index-link"
                className="inline-flex items-center justify-center h-11 px-5 rounded-full bg-cream-100 hover:bg-cream-200 text-ink text-[13.5px] font-bold border border-surface-line transition-all hover:scale-[1.01] active:scale-[0.99]"
              >
                Browse all FAQs
              </Link>
              <Link
                to="/check"
                data-testid="homepage-faq-cta-check"
                className="inline-flex items-center justify-center h-11 px-5 rounded-full bg-ink hover:bg-ink-soft text-cream-50 text-[13.5px] font-bold transition-all hover:scale-[1.01] active:scale-[0.99]"
              >
                Check your routine free →
              </Link>
            </div>
          </div>
        </div>

        {/* Right — accordion */}
        <div className="lg:col-span-8" data-animate="fade-up-2">
          <Accordion
            type="single"
            collapsible
            data-testid="homepage-faq-accordion"
            className="divide-y divide-surface-line border-y border-surface-line"
          >
            {FAQS.map((faq) => (
              <AccordionItem
                key={faq.slug}
                value={faq.slug}
                data-testid={`homepage-faq-item-${faq.slug}`}
                className="border-b-0"
              >
                <AccordionTrigger
                  data-testid={`homepage-faq-trigger-${faq.slug}`}
                  className="py-5 text-left text-[15.5px] sm:text-base font-semibold text-ink hover:no-underline hover:text-ink"
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`homepage-faq-content-${faq.slug}`}
                  className="pb-5 pr-2"
                >
                  <AnswerBody lines={faq.answer} />
                  <Link
                    to={`/faq/${faq.slug}`}
                    data-testid={`homepage-faq-readmore-${faq.slug}`}
                    className="inline-flex items-center gap-1.5 mt-4 text-[13px] font-semibold text-emerald-800 hover:text-emerald-900 transition-colors"
                  >
                    Read full answer →
                  </Link>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
